import { useEffect, useState } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import useEmblaCarousel from "embla-carousel-react";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { ZoomableImage } from "./ZoomableImage";

const arrowClass =
  "absolute top-1/2 -translate-y-1/2 grid place-items-center h-11 w-11 rounded-full bg-white/10 text-ivory transition-colors duration-300 hover:bg-white/20 z-20";

export const GalleryLightbox = ({ open, onOpenChange, gallery, startIndex = 0, onIndexChange }) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, watchDrag: false });
  const [index, setIndex] = useState(startIndex);

  useEffect(() => {
    if (open && emblaApi) {
      emblaApi.scrollTo(startIndex, true);
      setIndex(startIndex);
    }
  }, [open, emblaApi, startIndex]);

  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => {
      const i = emblaApi.selectedScrollSnap();
      setIndex(i);
      onIndexChange?.(i);
    };
    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", onSelect);
    return () => {
      emblaApi.off("select", onSelect);
      emblaApi.off("reInit", onSelect);
    };
  }, [emblaApi, onIndexChange]);

  if (!gallery || gallery.length === 0) return null;

  const current = gallery[index] ?? gallery[0];

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-[60] bg-black/90" />
        <DialogPrimitive.Content
          data-testid="pdp-lightbox"
          className="fixed inset-0 z-[60] outline-none"
          aria-describedby={undefined}
        >
          <DialogPrimitive.Title className="sr-only">
            {current.label ?? `${index + 1} of ${gallery.length}`}
          </DialogPrimitive.Title>
          <DialogPrimitive.Description className="sr-only">Product image zoom view</DialogPrimitive.Description>

          <button
            onClick={() => onOpenChange(false)}
            data-testid="pdp-lightbox-close"
            aria-label="Close zoom view"
            className="absolute top-5 right-5 z-20 grid place-items-center h-10 w-10 rounded-full bg-white/10 text-ivory transition-colors duration-300 hover:bg-white/20"
          >
            <X size={18} strokeWidth={1.6} />
          </button>

          <div className="absolute top-6 left-6 z-20 text-ivory/80 text-[0.68rem] font-medium uppercase tracking-[0.14em]">
            {index + 1} / {gallery.length}
          </div>

          <div ref={emblaRef} className="absolute inset-0 overflow-hidden">
            <div className="flex h-full">
              {gallery.map((g, i) => (
                <div key={i} className="min-w-0 h-full shrink-0 grow-0 basis-full">
                  <ZoomableImage
                    src={g.src}
                    alt={g.label ?? `Image ${i + 1}`}
                    label={g.label}
                    active={i === index}
                    onSwipe={(dir) => (dir > 0 ? emblaApi?.scrollNext() : emblaApi?.scrollPrev())}
                  />
                </div>
              ))}
            </div>
          </div>

          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 bg-white/10 text-ivory text-[0.68rem] uppercase tracking-[0.14em] px-3 py-1.5 rounded-full">
            {current.label}
          </div>

          <button
            onClick={() => emblaApi?.scrollPrev()}
            data-testid="pdp-lightbox-prev"
            aria-label="Previous image"
            className={`${arrowClass} left-5`}
          >
            <ChevronLeft size={20} strokeWidth={1.5} />
          </button>
          <button
            onClick={() => emblaApi?.scrollNext()}
            data-testid="pdp-lightbox-next"
            aria-label="Next image"
            className={`${arrowClass} right-5`}
          >
            <ChevronRight size={20} strokeWidth={1.5} />
          </button>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
};

export default GalleryLightbox;